/**
 * VisitTypeNoteConfigPanel (demo-only)
 * ──────────────────────────────────────────────────────────────────────
 * Mirrored entry point for the per-visit-type note configurator. Embedded
 * in:
 *   • Scheduler → Settings → Visit Types (per-type editor)
 *   • Lumina  → Settings → Preferences (full catalog + per-type editor)
 *
 * The same `VisitTypeNoteConfigEditor` is used in both contexts. In the
 * "preferences" surface we additionally render a left-rail visit type
 * catalog; in the "scheduler" surface the parent (VisitTypeSettings)
 * already drives the selected row, so we render only the editor.
 */
import { useState } from 'react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { useAppMode } from '../../../contexts/AppModeContext';
import { DEMO_VISIT_TYPES_CATALOG } from '../../../lib/fixtures/schedulerV2Demo';
import VisitTypeNoteConfigEditor from './VisitTypeNoteConfigEditor';
import { Lock } from 'lucide-react';

const VisitTypeNoteConfigPanel = ({ visitType, surface = 'scheduler' }) => {
  const { isDemo } = useAppMode();

  if (!isDemo) {
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-4 flex items-start gap-3" data-testid="vt-note-config-real-gate">
        <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center shrink-0">
          <Lock className="h-4 w-4 text-slate-500" />
        </div>
        <div>
          <p className="text-[12px] font-semibold text-slate-700">Note structure &amp; copy-forward</p>
          <p className="text-[10px] text-slate-500 mt-0.5">
            This configurator is currently available in Demo mode only. Real-mode support will
            arrive once the provider-preferences API is wired.
          </p>
        </div>
      </div>
    );
  }

  // Scheduler surface: parent chose the visit type, so just render the editor.
  if (surface === 'scheduler') {
    if (!visitType) return null;
    return <VisitTypeNoteConfigEditor visitType={visitType} surface="scheduler" />;
  }

  // Preferences surface: render a left-rail catalog + editor for the
  // currently selected row. The catalog is read from the same demo fixture
  // the Scheduler uses, so the two surfaces show an identical list.
  return <PreferencesCatalogAndEditor />;
};

const PreferencesCatalogAndEditor = () => {
  const [activeId, setActiveId] = useState(DEMO_VISIT_TYPES_CATALOG[0]?.id);
  const activeType = DEMO_VISIT_TYPES_CATALOG.find((v) => v.id === activeId) || DEMO_VISIT_TYPES_CATALOG[0];

  return (
    <div className="flex flex-col lg:flex-row gap-4" data-testid="vt-note-config-preferences">
      <aside className="w-full lg:w-60 shrink-0">
        <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2">Visit types</p>
        <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
          {DEMO_VISIT_TYPES_CATALOG.map((vt) => {
            const selected = vt.id === activeId;
            return (
              <button
                key={vt.id}
                type="button"
                onClick={() => setActiveId(vt.id)}
                className={cn(
                  'w-full text-left px-3 py-2 border-b border-slate-100 last:border-b-0 flex items-center gap-2 transition-colors',
                  selected ? 'bg-violet-50' : 'hover:bg-slate-50',
                )}
                style={{ cursor: 'pointer', borderLeft: selected ? '3px solid #6b5ce7' : '3px solid transparent' }}
                data-testid={`vt-pref-row-${vt.id}`}
              >
                <span className="flex-1">
                  <span className={cn('block text-[12px] font-medium', selected ? 'text-slate-900' : 'text-slate-700')}>{vt.name}</span>
                  <span className="block text-[9px] text-slate-400">{vt.abbreviation} · {vt.defaultDuration}m</span>
                </span>
                {vt.showAsDefault && <Badge className="text-[8px] bg-slate-100 text-slate-500 border-0">Default</Badge>}
              </button>
            );
          })}
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        {activeType ? (
          <VisitTypeNoteConfigEditor visitType={activeType} surface="preferences" />
        ) : (
          <div className="text-[11px] text-slate-500">Select a visit type to configure its note structure.</div>
        )}
      </div>
    </div>
  );
};

export default VisitTypeNoteConfigPanel;
